import {ActionReducer, createFeature, createReducer, createSelector, on, select} from '@ngrx/store';
import {EntityState, EntityAdapter, createEntityAdapter, Dictionary} from '@ngrx/entity';
import {Update} from "@ngrx/entity";
import {Service} from "../../models/service.model";
import {ServicesActions, ServicesAPiActions, ServicesChannelActions} from "./services.action";
import {uniq, uniqBy} from "lodash";

export interface ServicesState extends EntityState<Service> {
  cursor: string;
  isLoading: boolean;
  initialized: boolean;
  infiniteScrollServiceIds: string[]
}

const adapter: EntityAdapter<Service> = createEntityAdapter<Service>();

const initialState: ServicesState = adapter.getInitialState({
  cursor: null,
  isLoading: false,
  initialized: false,
  infiniteScrollServiceIds: []
});


const reducer: ActionReducer<ServicesState> = createReducer(
  initialState,

  on(ServicesChannelActions.notificationReceived, (state, {notification}) => {
    if (state.entities[notification.id]?.updatedAt < notification.updatedAt) {
      return adapter.setOne(notification, state);
    } else {
      return state;
    }
  }),

  on(ServicesChannelActions.partialNotificationReceived, (state, {notification}) => {

    if (state.entities[notification.id]?.updatedAt < notification.updatedAt) {
      const changes: Update<Service> = {
        id: notification.id,
        changes: {...notification}
      }
      return adapter.updateOne(changes, state);
    } else {
      return state;
    }
  }),

  on(ServicesAPiActions.getService, (state) => ({...state, isLoading: true})),

  on(ServicesAPiActions.getServiceSuccess, (state, {service}) => {
    return adapter.setOne(service, {...state, isLoading: false, initialized: true})
  }),

  on(ServicesAPiActions.getInfiniteScrollServices, (state) => ({...state, isLoading: true})),

  on(ServicesActions.updateService, (state, {service}) => {
    if (!!service) {
      const changes: Update<Service> = {
        id: service.id,
        changes: {...service}
      }
      return adapter.updateOne(changes, state);
    }
  }),

  on(ServicesActions.updateServices, (state, {services}) => {
    if (services.length) {
      const changes = [];
      services?.forEach((s) => {
        const change: Update<Service> = {
          id: s.id,
          changes: {...s}
        }
        changes.push(change)
      })
      return adapter.updateMany(changes, state);
    }
  }),

  on(ServicesAPiActions.getInfiniteScrollServicesSuccess, (state, {services, cursor}) => {
    return adapter.setMany(services,
      {
        ...state,
        infiniteScrollServiceIds: uniq([...state.infiniteScrollServiceIds, ...services.map(s => s.id)]),
        cursor: cursor,
        isLoading: false,
        initialized: true
      })
  }),

  on(ServicesAPiActions.getServices, (state) => ({...state, isLoading: true})),

  on(ServicesAPiActions.getServicesSuccess, (state, {services}) => {
    return adapter.setMany(services, {...state, isLoading: false, initialized: true})
  }),

  on(ServicesActions.setServices, (state, {services, isAppendFirst}) => {
    if (isAppendFirst) {
      const currentServicesInStore = Object.values(state.entities);
      const servicesArr = uniqBy([...services, ...currentServicesInStore], 'id');
      return adapter.setAll(servicesArr, state);
    } else {
      return adapter.setMany(services, state)
    }
  }),

  on(ServicesActions.setInfiniteScrollServiceIds, (state, {serviceIds, isAppendFirst}) => {
    let newState = state;
    const currentInfiniteScrollServiceIdsInStore = state.infiniteScrollServiceIds;

    if (isAppendFirst) {
      newState = {
        ...newState,
        infiniteScrollServiceIds: uniq([...serviceIds, ...currentInfiniteScrollServiceIdsInStore])
      }
    } else {
      newState = {
        ...newState,
        infiniteScrollServiceIds: uniq([...currentInfiniteScrollServiceIdsInStore, ...serviceIds])
      }
    }
    return newState;

  }),

  on(ServicesActions.removeServices, (state, {ids}) => adapter.removeMany(ids, state)),

  on(ServicesActions.removeAll, (state) => adapter.removeAll(state)),

  on(ServicesActions.clearStore, (state) => ({...initialState, initialized: false}))
);

export const servicesFeature = createFeature({
  name: 'Services',
  reducer,
  extraSelectors: ({
                     selectServicesState,
                     selectEntities,
                     selectCursor,
                     selectIsLoading,
                     selectInitialized,
                     selectInfiniteScrollServiceIds,
                   }) => ({
    ...adapter.getSelectors(selectServicesState),
    selectServices: () => createSelector(
      selectEntities,
      (entities) => Object.values(entities),
    ),
    selectInfiniteScrollServices: () => createSelector(
      selectEntities,
      selectInfiniteScrollServiceIds,
      (entities, infiniteScrollServiceIds) => {
        const infiniteScrollServices = [];
        infiniteScrollServiceIds.forEach(id => {
          if (!!entities[id]) {
            infiniteScrollServices.push(entities[id]);
          }
        })
        return infiniteScrollServices;
      },
    ),
    selectServicesById: (ids: string[]) => createSelector(
      selectEntities,
      (entities) => {
        const services = [];
        ids?.forEach(id => {
          if (!!entities[id]) {
            services.push(entities[id]);
          }
        })
        return services;
      },
    ),
    selectService: (id: string) => createSelector(
      selectEntities,
      (entities: Dictionary<Service>) => entities[id],
    ),
    selectCursor: () => createSelector(
      selectCursor,
      (cursor) => cursor
    ),
    selectIsLoading: () => createSelector(
      selectIsLoading,
      (isLoading) => isLoading
    ),
    selectInitialized: () => createSelector(
      selectInitialized,
      (initialized) => initialized
    )
  })
});
